import React, { useState } from 'react';

import { nativeLensId } from '~/nativeLenses/nativeLensId';

import { useGetNativeChoices } from '~/hooks/apis/reporting/useGetNativeChoices';

import type { LensFilterValues } from '~/types/leLens';

import AttributeSelect, { type AttributeSelectProps } from './AttributeSelect';
import { marketingChoices } from './choices';

/** Server search covers the full narrowed domain, with a bounded page at a time. */
export default function CascadingAttributeSelect({
  filterKey,
  filters,
  ...props
}: AttributeSelectProps & {
  filterKey: string;
  filters?: LensFilterValues;
}) {
  const [search, setSearch] = useState<{ term: string; after?: string; scope: string }>({ term: '', scope: '' });
  const scope = JSON.stringify(filters);
  const active = search.scope === scope ? search : { term: '', after: undefined };
  const remote =
    ['campaign_bk', 'adgroup_name', 'adgroup_bk'].includes(filterKey) ||
    (props.total !== undefined && props.total > props.options.length);
  const searching = remote && Boolean(active.term || active.after);
  const query = useGetNativeChoices(searching ? nativeLensId('performance-marketing') : undefined, {
    ...filters,
    pm_choice_query: JSON.stringify({ kind: filterKey, term: active.term, after: active.after }),
  });
  const choices = marketingChoices(query.current ? query.data?.cards : undefined);
  const options = searching ? (choices.options[filterKey] ?? []) : props.options;
  const hasMore = searching ? choices.hasMore?.[filterKey] : props.hasMore;
  const cursor = searching ? choices.cursors?.[filterKey] : props.cursor;
  return (
    <AttributeSelect
      {...props}
      options={options}
      serverSearch={remote}
      searchValue={active.term}
      onSearch={(term) => setSearch({ term, after: undefined, scope })}
      onNextPage={
        hasMore && cursor !== undefined ? () => setSearch({ term: active.term, after: cursor, scope }) : undefined
      }
      loading={searching ? !query.current && !query.isError : props.loading}
      searchError={searching ? query.isError : props.searchError}
    />
  );
}
